import { createClient } from '@supabase/supabase-js';

/**
 * Cliente Supabase único (singleton) do Portal do Cidadão.
 * Aponta para o MESMO projeto Supabase do PETBEA ADM — uma só fonte de verdade.
 * As credenciais públicas ficam nas variáveis de ambiente do front.
 */
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

/**
 * Sessão "zumbi": um refresh token velho/revogado que ficou guardado no navegador.
 * O cliente Supabase tenta renová-lo sozinho (auto-refresh) e o servidor responde
 * "Invalid Refresh Token: Refresh Token Not Found". Como essa renovação acontece
 * fora do fluxo da página, o erro vira um erro global não tratado.
 *
 * Aqui a gente: (1) reconhece esse erro específico, (2) limpa a sessão morta
 * localmente (sem rede, pois o token já não vale nada) e (3) evita que o erro
 * apareça para o cidadão. Assim o ciclo de renovação se encerra e não fica repetindo.
 */
function ehErroRefreshTokenInvalido(mensagem: string): boolean {
  const m = mensagem.toLowerCase();
  return (
    m.includes('invalid refresh token') ||
    m.includes('refresh token not found') ||
    m.includes('refresh_token_not_found')
  );
}

let limpandoSessaoInvalida = false;

async function limparSessaoInvalida(): Promise<void> {
  if (limpandoSessaoInvalida) return;
  limpandoSessaoInvalida = true;
  try {
    // scope "local": só limpa o storage do navegador, não tenta falar com o servidor.
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    /* o token já era inválido — nada mais a fazer */
  } finally {
    limpandoSessaoInvalida = false;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (evento) => {
    const motivo: unknown = evento.reason;
    const mensagem =
      typeof motivo === 'string' ? motivo : (motivo as { message?: string } | null)?.message ?? '';
    if (ehErroRefreshTokenInvalido(mensagem)) {
      evento.preventDefault();
      void limparSessaoInvalida();
    }
  });
}